import React, { useEffect, useState } from 'react';
/**
 * 债务人步骤组件
 * 职责：
 * - 管理个人/企业债务人列表及其文件上传/删除
 * - 个人“其他证件说明”仅用于辅助识别，限制20字符
 * - 上传文件在草稿前走临时业务类型（debt_evidence_temp），发布后移动至正式业务类型（debt_evidence）
 */
import { useSelector } from 'react-redux';
import { Form, Input, Select, Button, Space, Divider, Card, Upload, message, Popconfirm } from 'antd';
import provinces from 'china-division/dist/provinces.json';
import cities from 'china-division/dist/cities.json';
import { NonPerformanceReasonOptions, toSelectOptions } from '../../../../constants/debtEnums';
import { uploadBusinessFile, deleteBusinessFile } from '../../../../services/debt';

const provinceOptions = provinces.map(p => ({ value: p.name, label: p.name, code: p.code }));
const cityOptionsMapByName = provinces.reduce((acc, p) => {
  const list = cities.filter(c => c.provinceCode === p.code).map(c => ({ value: c.name, label: c.name }));
  acc[p.name] = list;
  return acc;
}, {});

const DebtorsStep = ({ value, onChange, onNext, onPrev, debtId, ensureDebtId }) => {
  const [form] = Form.useForm();
  const [deletingKey, setDeletingKey] = useState(null);
  const [deleteErrors, setDeleteErrors] = useState({});
  const { user } = useSelector(state => state.auth || {});

  // 提取文件名，用于只读展示（不暴露完整URL）
  const fileNameFromUrl = (url) => {
    if (!url) return '';
    try {
      const base = String(url).split('?')[0];
      return base.substring(base.lastIndexOf('/') + 1);
    } catch (_) {
      return String(url);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      debtorsPersonal: value.debtorsPersonal,
      debtorsEnterprise: value.debtorsEnterprise,
    });
  }, [value.debtorsPersonal, value.debtorsEnterprise, form]);

  const handleValuesChange = (_, allValues) => {
    onChange(prev => ({
      ...prev,
      debtorsPersonal: allValues.debtorsPersonal || [],
      debtorsEnterprise: allValues.debtorsEnterprise || [],
    }));
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      const personalCount = (form.getFieldValue('debtorsPersonal') || []).length;
      const enterpriseCount = (form.getFieldValue('debtorsEnterprise') || []).length;
      if (personalCount + enterpriseCount < 1) {
        throw new Error('至少添加一名债务人（个人或企业）');
      }
      onNext();
    } catch (e) {
      if (e?.message) {
        message.error(e.message);
      }
    }
  };

  // 上传封装：根据列表名/索引/字段写入表单；草稿阶段统一使用临时业务类型
  const mkUpload = (listName, index, field) => ({
    name: 'file',
    accept: '.jpg,.jpeg,.png,.pdf',
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const userId = user?.id;
        if (!userId) {
          message.error('未登录或用户信息缺失');
          onError && onError(new Error('用户未登录'));
          return;
        }
        const { data } = await uploadBusinessFile(file, 'debt_evidence_temp', userId);
        if (data?.objectKey) {
          const list = form.getFieldValue(listName) || [];
          const next = [...list];
          const item = { ...(next[index] || {}) };
          item[field] = data.objectKey;
          next[index] = item;
          form.setFieldsValue({ [listName]: next });
          onChange(prev => ({ ...prev, [listName]: next }));
          message.success('文件上传成功');
        }
        onSuccess && onSuccess(data);
      } catch (err) {
        message.error('文件上传失败');
        onError && onError(err);
      }
    },
  });

  // 删除封装：区分临时/正式文件并选择对应业务类型与业务ID
  const mkDelete = (listName, index, field) => async () => {
    try {
      const key = `${listName}-${index}-${field}`;
      setDeletingKey(key);
      setDeleteErrors(prev => ({ ...prev, [key]: undefined }));
      const userId = user?.id;
      if (!userId) {
        message.error('未登录或用户信息缺失');
        setDeletingKey(null);
        return;
      }
      const list = form.getFieldValue(listName) || [];
      const item = list[index] || {};
      const url = item[field];
      if (!url) {
        message.error('暂无可删除的文件');
        setDeletingKey(null);
        return;
      }
      const isTemp = String(url).includes('/debt_evidence_temp/');
      const businessType = isTemp ? 'debt_evidence_temp' : 'debt_evidence';
      const bId = isTemp ? userId : (debtId || await ensureDebtId());
      await deleteBusinessFile(url, businessType, bId);
      const next = [...list];
      next[index] = { ...(next[index] || {}), [field]: undefined };
      form.setFieldsValue({ [listName]: next });
      onChange(prev => ({ ...prev, [listName]: next }));
      message.success('已删除文件');
      setDeletingKey(null);
    } catch (err) {
      message.error('删除文件失败');
      const key = `${listName}-${index}-${field}`;
      setDeleteErrors(prev => ({ ...prev, [key]: (err?.message || '删除文件失败') }));
      setDeletingKey(null);
    }
  };

  // 删除个人债务人：尝试清理其身份证相关已上传文件
  const handleRemoveDebtorPersonal = async (name, remove) => {
    try {
      const list = form.getFieldValue('debtorsPersonal') || [];
      const item = list[name] || {};
      const delOne = async (url) => {
        if (!url) return;
        const isTemp = String(url).includes('/debt_evidence_temp/');
        const businessType = isTemp ? 'debt_evidence_temp' : 'debt_evidence';
        const bId = isTemp ? (user?.id) : (debtId || await ensureDebtId());
        try { await deleteBusinessFile(url, businessType, bId); } catch (_) {}
      };
      await delOne(item.id_card_front_url);
      await delOne(item.id_card_back_url);
      await delOne(item.other_id_urls);
    } catch (_) {}
    const next = [...(form.getFieldValue('debtorsPersonal') || [])];
    next.splice(name, 1);
    form.setFieldsValue({ debtorsPersonal: next });
    onChange(prev => ({ ...prev, debtorsPersonal: next }));
    remove(name);
    message.success('已删除个人债务人');
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        debtorsPersonal: value.debtorsPersonal,
        debtorsEnterprise: value.debtorsEnterprise,
      }}
      onValuesChange={handleValuesChange}
    >
      <Card size="small" title="个人债务人">
        <Form.List name="debtorsPersonal">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card key={key} className="debtor-card" size="small" style={{ marginBottom: 12 }}>
                  <Space wrap>
                    <Form.Item {...restField} name={[name, 'name']} label="姓名" rules={[{ required: true, message: '请填写姓名' }]}>
                      <Input style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'id_no']} label="证件号" rules={[{ required: true, message: '请填写证件号' }]}>
                      <Input style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'phone']} label="电话">
                      <Input style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'email']} label="邮箱">
                      <Input style={{ width: 240 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'province']} label="省">
                      <Select 
                        allowClear 
                        style={{ width: 180 }}
                        options={provinceOptions}
                        onChange={(val) => {
                          const list = form.getFieldValue('debtorsPersonal') || [];
                          const next = [...list];
                          next[name] = { ...(next[name] || {}), province: val, city: undefined };
                          form.setFieldsValue({ debtorsPersonal: next });
                        }}
                      />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'city']} label="市">
                      <Select 
                        allowClear 
                        style={{ width: 180 }}
                        disabled={!form.getFieldValue(['debtorsPersonal', name, 'province'])}
                        options={(() => {
                          const p = form.getFieldValue(['debtorsPersonal', name, 'province']);
                          return p ? (cityOptionsMapByName[p] || []) : [];
                        })()}
                      />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'address']} label="详细地址">
                      <Input style={{ width: 320 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'id_card_front_url']} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item label="身份证正面">
                      <Input readOnly style={{ width: 260 }} value={fileNameFromUrl(form.getFieldValue(['debtorsPersonal', name, 'id_card_front_url']))} placeholder="未上传" />
                    </Form.Item>
                    <Upload {...mkUpload('debtorsPersonal', name, 'id_card_front_url')}>
                      <Button>上传正面</Button>
                    </Upload>
                    <Popconfirm title="确认删除该文件？" onConfirm={mkDelete('debtorsPersonal', name, 'id_card_front_url')} disabled={!form.getFieldValue(['debtorsPersonal', name, 'id_card_front_url'])}>
                      <Button danger disabled={!form.getFieldValue(['debtorsPersonal', name, 'id_card_front_url'])} loading={deletingKey===`debtorsPersonal-${name}-id_card_front_url`}>删除正面</Button>
                    </Popconfirm>
                    {deleteErrors[`debtorsPersonal-${name}-id_card_front_url`] && (
                      <Button type="link" onClick={mkDelete('debtorsPersonal', name, 'id_card_front_url')}>重试</Button>
                    )}
                    <Form.Item {...restField} name={[name, 'id_card_back_url']} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item label="身份证反面">
                      <Input readOnly style={{ width: 260 }} value={fileNameFromUrl(form.getFieldValue(['debtorsPersonal', name, 'id_card_back_url']))} placeholder="未上传" />
                    </Form.Item>
                    <Upload {...mkUpload('debtorsPersonal', name, 'id_card_back_url')}>
                      <Button>上传反面</Button>
                    </Upload>
                    <Popconfirm title="确认删除该文件？" onConfirm={mkDelete('debtorsPersonal', name, 'id_card_back_url')} disabled={!form.getFieldValue(['debtorsPersonal', name, 'id_card_back_url'])}>
                      <Button danger disabled={!form.getFieldValue(['debtorsPersonal', name, 'id_card_back_url'])} loading={deletingKey===`debtorsPersonal-${name}-id_card_back_url`}>删除反面</Button>
                    </Popconfirm>
                    {deleteErrors[`debtorsPersonal-${name}-id_card_back_url`] && (
                      <Button type="link" onClick={mkDelete('debtorsPersonal', name, 'id_card_back_url')}>重试</Button>
                    )}
                    <Form.Item {...restField} name={[name, 'other_id_urls']} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item label="其他证件">
                      <Input
                        readOnly
                        style={{ width: 260 }}
                        value={fileNameFromUrl(form.getFieldValue(['debtorsPersonal', name, 'other_id_urls']))}
                        placeholder="未上传"
                      />
                    </Form.Item>
                    <Upload {...mkUpload('debtorsPersonal', name, 'other_id_urls')}>
                      <Button>上传其他证件</Button>
                    </Upload>
                    <Popconfirm
                      title="确认删除该文件？"
                      onConfirm={mkDelete('debtorsPersonal', name, 'other_id_urls')}
                      disabled={!form.getFieldValue(['debtorsPersonal', name, 'other_id_urls'])}
                    >
                      <Button
                        danger
                        disabled={!form.getFieldValue(['debtorsPersonal', name, 'other_id_urls'])}
                        loading={deletingKey===`debtorsPersonal-${name}-other_id_urls`}
                      >
                        删除其他证件
                      </Button>
                    </Popconfirm>
                    {deleteErrors[`debtorsPersonal-${name}-other_id_urls`] && (
                      <Button type="link" onClick={mkDelete('debtorsPersonal', name, 'other_id_urls')}>重试</Button>
                    )}
                    {/* 其他证件说明：前端限制20字符，后端保存前亦做截断以防超长 */}
                    <Form.Item {...restField} name={[name, 'other_id_desc']} label="其他证件说明" rules={[{ max: 20, message: '最多输入20个字符' }]}> 
                      <Input style={{ width: 320 }} placeholder="可识别其身份的如驾照、银行账号、支付宝、微信账号等" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'non_performance_reason']} label="未履行原因">
                      <Select allowClear style={{ width: 240 }} options={toSelectOptions(NonPerformanceReasonOptions)} />
                    </Form.Item>
                  </Space>
                  <div style={{ textAlign: 'right' }}>
                    <Popconfirm title="确认删除该债务人？" onConfirm={() => handleRemoveDebtorPersonal(name, remove)}>
                      <Button danger type="link">删除</Button>
                    </Popconfirm>
                  </div>
                </Card>
              ))}
              <Button type="dashed" onClick={() => add()} block>
                添加个人债务人
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      <Divider />

      <Card size="small" title="企业债务人">
        <Form.List name="debtorsEnterprise">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card key={key} className="debtor-card" size="small" style={{ marginBottom: 12 }}>
                  <Space wrap>
                    <Form.Item {...restField} name={[name, 'company_name']} label="企业名称" rules={[{ required: true, message: '请填写企业名称' }]}>
                      <Input style={{ width: 300 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'social_credit_code']} label="统一社会信用代码" rules={[{ required: true, message: '请填写信用代码' }]}>
                      <Input style={{ width: 260 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'legal_person_name']} label="法定代表人姓名">
                      <Input style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'legal_person_id_no']} label="法定代表人身份证号">
                      <Input style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'phone']} label="电话">
                      <Input style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'email']} label="邮箱">
                      <Input style={{ width: 240 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'province']} label="省">
                      <Select 
                        allowClear 
                        style={{ width: 180 }}
                        options={provinceOptions}
                        onChange={(val) => {
                          const list = form.getFieldValue('debtorsEnterprise') || [];
                          const next = [...list];
                          next[name] = { ...(next[name] || {}), province: val, city: undefined };
                          form.setFieldsValue({ debtorsEnterprise: next });
                        }}
                      />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'city']} label="市">
                      <Select 
                        allowClear 
                        style={{ width: 180 }}
                        disabled={!form.getFieldValue(['debtorsEnterprise', name, 'province'])}
                        options={(() => {
                          const p = form.getFieldValue(['debtorsEnterprise', name, 'province']);
                          return p ? (cityOptionsMapByName[p] || []) : [];
                        })()}
                      />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'address']} label="详细地址">
                      <Input style={{ width: 320 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'non_performance_reason']} label="未履行原因">
                      <Select allowClear style={{ width: 240 }} options={toSelectOptions(NonPerformanceReasonOptions)} />
                    </Form.Item>
                  </Space>
                  <div style={{ textAlign: 'right' }}>
                    <Button danger type="link" onClick={() => remove(name)}>删除</Button>
                  </div>
                </Card>
              ))}
              <Button type="dashed" onClick={() => add()} block>
                添加企业债务人
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      <Form.Item style={{ marginTop: 16 }}>
        <Space>
          <Button onClick={onPrev}>上一步</Button>
          <Button type="primary" onClick={handleNext}>下一步</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default DebtorsStep;
